import dayjs from 'dayjs'
import { groupBy, orderBy, range, sumBy } from 'lodash'
import type { PaytrieAPI } from '@paytrie/client'

export const HISTORY_DAYS = 90

export type MaintenanceWindowStatus = 'scheduled' | 'in-progress' | 'completed'

export const getWindowStatus = ({
  window,
  now,
}: {
  window: PaytrieAPI.MaintenanceWindow
  now: Date
}): MaintenanceWindowStatus => {
  if (dayjs(now).isBefore(window.startsAt)) return 'scheduled'
  if (window.endsAt && !dayjs(now).isBefore(window.endsAt)) return 'completed'
  return 'in-progress'
}

export type DayAvailability = {
  day: Date
  windows: PaytrieAPI.MaintenanceWindow[]
  maintenanceMs: number
}

const getRecentDays = (now: Date) =>
  range(HISTORY_DAYS - 1, -1, -1).map((daysAgo) =>
    dayjs(now).subtract(daysAgo, 'day').startOf('day')
  )

const getOverlapMs = ({
  window,
  day,
  now,
}: {
  window: PaytrieAPI.MaintenanceWindow
  day: dayjs.Dayjs
  now: Date
}) => {
  const start = dayjs(window.startsAt)
  const end = window.endsAt ? dayjs(window.endsAt) : dayjs(now)
  const overlapStart = start.isAfter(day) ? start : day
  const dayEnd = day.add(1, 'day')
  const overlapEnd = end.isBefore(dayEnd) ? end : dayEnd
  return Math.max(0, overlapEnd.diff(overlapStart))
}

export const getDailyAvailability = ({
  windows,
  now,
}: {
  windows: PaytrieAPI.MaintenanceWindow[]
  now: Date
}): DayAvailability[] => {
  const started = windows.filter(
    (window) => getWindowStatus({ window, now }) !== 'scheduled'
  )
  return getRecentDays(now).map((day) => {
    const overlaps = started
      .map((window) => ({ window, ms: getOverlapMs({ window, day, now }) }))
      .filter(({ ms }) => ms > 0)
    return {
      day: day.toDate(),
      windows: overlaps.map(({ window }) => window),
      maintenanceMs: sumBy(overlaps, 'ms'),
    }
  })
}

export const partitionWindows = ({
  windows,
  now,
}: {
  windows: PaytrieAPI.MaintenanceWindow[]
  now: Date
}) => {
  const byStatus = groupBy(windows, (window) =>
    getWindowStatus({ window, now })
  )
  return {
    inProgress: byStatus['in-progress'] ?? [],
    upcoming: orderBy(byStatus.scheduled ?? [], 'startsAt', 'asc'),
    past: orderBy(byStatus.completed ?? [], 'startsAt', 'desc'),
  }
}

export const getRecentPastWindows = ({
  windows,
  now,
}: {
  windows: PaytrieAPI.MaintenanceWindow[]
  now: Date
}) => {
  const cutoff = dayjs(now)
    .subtract(HISTORY_DAYS - 1, 'day')
    .startOf('day')
  return windows.filter((window) => dayjs(window.endsAt ?? now).isAfter(cutoff))
}

export const getDurationMs = ({
  startsAt,
  endsAt,
}: {
  startsAt: string
  endsAt: string
}) => dayjs(endsAt).diff(startsAt)

export const formatCountdown = ({
  until,
  now,
}: {
  until: string
  now: Date
}) => {
  const totalSeconds = Math.max(0, dayjs(until).diff(now, 'second'))
  return [
    Math.floor(totalSeconds / 3600),
    Math.floor((totalSeconds % 3600) / 60),
    totalSeconds % 60,
  ]
    .map((part) => String(part).padStart(2, '0'))
    .join(':')
}
